import { type ReactNode } from 'react';
import { type JSX } from 'react/jsx-runtime';

import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { TriggeredMessageValidator } from './triggered-message-validator';

/**
 * Renders {@link TriggeredMessageValidator} when `waitForTrigger` is provided (i.e. the message is
 * client-triggered), otherwise renders `children` unchanged. Lets callers slot the validator into
 * an existing JSX tree without forking the tree into "triggered" and "non-triggered" branches.
 *
 * IMPORTANT: this wrapper must be mounted BELOW the Suspense boundary that owns the lazy template,
 * so the validator only mounts after the template's chunk has resolved. Otherwise the validator
 * commits with the Suspense fallback as its children (template hasn't executed yet, ref is false),
 * and when the chunk later resolves only the Suspense subtree re-renders — the validator above
 * never sees the ref flip to true and stays stuck in 'pending' with `display:none`. See PO-7815
 * follow-up.
 */
export const MaybeTriggeredMessageValidator = ({
	children,
	waitForTrigger,
}: {
	children: ReactNode;
	waitForTrigger?: WaitForTriggerFunction;
}): JSX.Element => {
	if (!waitForTrigger) {
		return <>{children}</>;
	}
	return (
		<TriggeredMessageValidator waitForTrigger={waitForTrigger}>
			{children}
		</TriggeredMessageValidator>
	);
};
